// C:\Users\clint\f1-tipping-site\frontend\src\pages\DebugPage.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';

const DebugPage = () => {
  const [raceId, setRaceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [h2hConfig, setH2hConfig] = useState(null);
  const [raceResults, setRaceResults] = useState(null);
  const [calculatedResults, setCalculatedResults] = useState(null);
  const [driverPositions, setDriverPositions] = useState(null);
  const [teamPositions, setTeamPositions] = useState(null);
  const [error, setError] = useState('');

  // Calculate head-to-head results when both config and results are available
  useEffect(() => {
    if (h2hConfig && raceResults && raceResults.result) {
      const { calculated, driversData, teamsData } = calculateHeadToHeadWinners(h2hConfig, raceResults.result);
      setCalculatedResults(calculated);
      setDriverPositions(driversData);
      setTeamPositions(teamsData);
    }
  }, [h2hConfig, raceResults]);

  // Function to calculate head-to-head winners based on race positions
  const calculateHeadToHeadWinners = (h2hConfig, raceResult) => {
    if (!h2hConfig || !h2hConfig.configuration || !raceResult) {
      return { calculated: null, driversData: null, teamsData: null };
    }

    // Create a copy of h2hConfig to modify
    const calculatedResults = { ...h2hConfig };
    
    // Ensure results structure exists
    if (!calculatedResults.results) {
      calculatedResults.results = {};
    }
    
    let driversData = null;
    let teamsData = null;
    
    // Log the entire race result for debugging
    console.log('Race Result Object:', raceResult);
    
    // Specifically check for the presence of a results array
    if (Array.isArray(raceResult.results)) {
      console.log('Results array found with length:', raceResult.results.length);
      
      // Log a sample of the first result to understand structure
      if (raceResult.results.length > 0) {
        console.log('Sample result structure:', raceResult.results[0]);
      }
    } else {
      console.log('No results array found in race result');
    }
    
    // Create driver head-to-head results
    if (h2hConfig.configuration.drivers) {
      calculatedResults.results.drivers = {};
      driversData = {};
      
      const driver1Id = h2hConfig.configuration.drivers.driver1.driverId;
      const driver2Id = h2hConfig.configuration.drivers.driver2.driverId;
      
      // Find positions of drivers in the results array
      let driver1Position = -1;
      let driver2Position = -1;
      let driver1Result = null;
      let driver2Result = null;
      
      // Look in the results array if available
      if (Array.isArray(raceResult.results)) {
        driver1Result = raceResult.results.find(r => r.driverId === driver1Id);
        driver2Result = raceResult.results.find(r => r.driverId === driver2Id);
        
        if (driver1Result) {
          driver1Position = parseInt(driver1Result.position);
        }
        
        if (driver2Result) {
          driver2Position = parseInt(driver2Result.position);
        }
      }
      
      console.log('Driver results from results array:', {
        driver1: { id: driver1Id, position: driver1Position, result: driver1Result },
        driver2: { id: driver2Id, position: driver2Position, result: driver2Result }
      });
      
      // Save driver position data for display
      driversData = {
        driver1: {
          id: driver1Id,
          name: h2hConfig.configuration.drivers.driver1.name,
          position: driver1Position,
          foundInResults: !!driver1Result,
          constructor: driver1Result ? driver1Result.constructorId : 'Unknown'
        },
        driver2: {
          id: driver2Id,
          name: h2hConfig.configuration.drivers.driver2.name,
          position: driver2Position,
          foundInResults: !!driver2Result,
          constructor: driver2Result ? driver2Result.constructorId : 'Unknown'
        }
      };
      
      console.log('Driver Positions:', driversData);
      
      // Determine winner based on position (lower position = better)
      if (driver1Position > 0 && driver2Position > 0) {
        if (driver1Position < driver2Position) {
          calculatedResults.results.drivers.winner = driver1Id;
        } else if (driver2Position < driver1Position) {
          calculatedResults.results.drivers.winner = driver2Id;
        } else {
          // Tie
          calculatedResults.results.drivers.winner = 'tie';
        }
      } else if (driver1Position > 0) {
        // Only driver 1 finished
        calculatedResults.results.drivers.winner = driver1Id;
      } else if (driver2Position > 0) {
        // Only driver 2 finished
        calculatedResults.results.drivers.winner = driver2Id;
      } else {
        // Neither finished
        calculatedResults.results.drivers.winner = 'neither';
      }
    }
    
    // Create team head-to-head results
    if (h2hConfig.configuration.teams) {
      calculatedResults.results.teams = {};
      teamsData = {};
      
      const team1Id = h2hConfig.configuration.teams.team1.constructorId;
      const team2Id = h2hConfig.configuration.teams.team2.constructorId;
      
      let team1Drivers = [];
      let team2Drivers = [];
      
      // Find all drivers for each team in the results array
      if (Array.isArray(raceResult.results)) {
        team1Drivers = raceResult.results
          .filter(driver => driver.constructorId === team1Id)
          .map(driver => ({ 
            id: driver.driverId, 
            position: parseInt(driver.position),
            name: `${driver.givenName} ${driver.familyName}` 
          }));
          
        team2Drivers = raceResult.results
          .filter(driver => driver.constructorId === team2Id)
          .map(driver => ({ 
            id: driver.driverId, 
            position: parseInt(driver.position),
            name: `${driver.givenName} ${driver.familyName}`
          }));
      }
      
      console.log('Team drivers found:', {
        team1: { id: team1Id, drivers: team1Drivers },
        team2: { id: team2Id, drivers: team2Drivers }
      });
      
      // Calculate average positions
      let team1AvgPos = Number.MAX_SAFE_INTEGER;
      let team2AvgPos = Number.MAX_SAFE_INTEGER;
      
      if (team1Drivers.length > 0) {
        team1AvgPos = team1Drivers.reduce((sum, driver) => sum + driver.position, 0) / team1Drivers.length;
      }
      
      if (team2Drivers.length > 0) {
        team2AvgPos = team2Drivers.reduce((sum, driver) => sum + driver.position, 0) / team2Drivers.length;
      }
      
      teamsData = {
        team1: {
          id: team1Id,
          name: h2hConfig.configuration.teams.team1.name,
          driversCount: team1Drivers.length,
          drivers: team1Drivers.map(d => `${d.name} (P${d.position})`).join(', ') || 'No drivers found',
          positions: team1Drivers.map(d => d.position).join(', ') || 'N/A',
          averagePosition: team1Drivers.length > 0 ? team1AvgPos.toFixed(1) : 'N/A'
        },
        team2: {
          id: team2Id,
          name: h2hConfig.configuration.teams.team2.name,
          driversCount: team2Drivers.length,
          drivers: team2Drivers.map(d => `${d.name} (P${d.position})`).join(', ') || 'No drivers found',
          positions: team2Drivers.map(d => d.position).join(', ') || 'N/A',
          averagePosition: team2Drivers.length > 0 ? team2AvgPos.toFixed(1) : 'N/A'
        }
      };
      
      console.log('Team positions:', {
        team1: { id: team1Id, avgPos: team1AvgPos },
        team2: { id: team2Id, avgPos: team2AvgPos }
      });
      
      // Determine winner based on average position
      if (team1Drivers.length > 0 && team2Drivers.length > 0) {
        if (team1AvgPos < team2AvgPos) {
          calculatedResults.results.teams.winner = team1Id;
        } else if (team2AvgPos < team1AvgPos) {
          calculatedResults.results.teams.winner = team2Id;
        } else {
          // Use best driver as tiebreaker
          const team1Best = Math.min(...team1Drivers.map(d => d.position));
          const team2Best = Math.min(...team2Drivers.map(d => d.position));
          
          console.log('Team best positions (tiebreaker):', {
            team1: team1Best,
            team2: team2Best
          });
          
          if (team1Best < team2Best) {
            calculatedResults.results.teams.winner = team1Id;
          } else if (team2Best < team1Best) {
            calculatedResults.results.teams.winner = team2Id;
          } else {
            calculatedResults.results.teams.winner = 'tie';
          }
        }
      } else if (team1Drivers.length > 0) {
        calculatedResults.results.teams.winner = team1Id;
      } else if (team2Drivers.length > 0) {
        calculatedResults.results.teams.winner = team2Id;
      } else {
        calculatedResults.results.teams.winner = 'neither';
      }
    }
    
    return { 
      calculated: calculatedResults,
      driversData,
      teamsData
    };
  };

  // Function to fetch both head-to-head config and race results
  const fetchData = async () => {
    if (!raceId) {
      setError('Please enter a race ID');
      return;
    }
    
    setLoading(true);
    setError('');
    setH2hConfig(null);
    setRaceResults(null);
    setCalculatedResults(null);
    setDriverPositions(null);
    setTeamPositions(null);
    
    try {
      // Fetch head-to-head configuration
      const h2hResponse = await apiClient.get(`/api/head-to-head/${raceId}`);
      console.log('H2H Config:', h2hResponse.data);
      setH2hConfig(h2hResponse.data);
      
      // Fetch race results from the scores endpoint
      try {
        const resultsResponse = await apiClient.get(`/api/scores/race/${raceId}`);
        console.log('Race Results:', resultsResponse.data);
        setRaceResults(resultsResponse.data);
      } catch (resultsError) {
        console.error('Error fetching race results:', resultsError);
        setError('Failed to load race results. Head-to-head configuration was loaded successfully.');
      }
    } catch (h2hError) {
      console.error('Error fetching head-to-head configuration:', h2hError);
      setError('Failed to load head-to-head configuration');
      
      // Try to fetch race results anyway
      try {
        const resultsResponse = await apiClient.get(`/api/scores/race/${raceId}`);
        console.log('Race Results:', resultsResponse.data);
        setRaceResults(resultsResponse.data);
      } catch (resultsError) {
        console.error('Error fetching race results:', resultsError);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Function to simulate head-to-head calculation with mock data
  const simulateCalculation = () => {
    if (!h2hConfig) {
      setError('Please fetch head-to-head configuration first');
      return;
    }
    
    // Create a copy of h2hConfig
    const calculated = { ...h2hConfig };
    
    // Ensure results structure exists
    if (!calculated.results) {
      calculated.results = {};
    }
    
    // Create mock driver data
    let driversData = null;
    if (h2hConfig.configuration?.drivers) {
      calculated.results.drivers = {};
      
      const driver1Id = h2hConfig.configuration.drivers.driver1.driverId;
      const driver2Id = h2hConfig.configuration.drivers.driver2.driverId;
      
      // For Doohan vs Bortoleto, use actual positions from the data
      if (driver1Id === 'doohan' && driver2Id === 'bortoleto') {
        driversData = {
          driver1: {
            id: driver1Id,
            name: h2hConfig.configuration.drivers.driver1.name,
            position: 13, // Actual position from data
            foundInResults: true,
            constructor: 'alpine'
          },
          driver2: {
            id: driver2Id,
            name: h2hConfig.configuration.drivers.driver2.name,
            position: 14, // Actual position from data
            foundInResults: true,
            constructor: 'sauber'
          }
        };
        
        // Driver 1 (Doohan) finished ahead, so is the winner
        calculated.results.drivers.winner = driver1Id;
      } else {
        // Random positions between 1-20 for other driver combos
        const driver1Position = Math.floor(Math.random() * 20) + 1;
        const driver2Position = Math.floor(Math.random() * 20) + 1;
        
        driversData = {
          driver1: {
            id: driver1Id,
            name: h2hConfig.configuration.drivers.driver1.name,
            position: driver1Position,
            foundInResults: true,
            constructor: 'unknown'
          },
          driver2: {
            id: driver2Id,
            name: h2hConfig.configuration.drivers.driver2.name,
            position: driver2Position,
            foundInResults: true,
            constructor: 'unknown'
          }
        };
        
        // Determine winner based on position
        if (driver1Position < driver2Position) {
          calculated.results.drivers.winner = driver1Id;
        } else if (driver2Position < driver1Position) {
          calculated.results.drivers.winner = driver2Id;
        } else {
          // Tie
          calculated.results.drivers.winner = 'tie';
        }
      }
    }
    
    // Create mock team data
    let teamsData = null;
    if (h2hConfig.configuration?.teams) {
      calculated.results.teams = {};
      
      const team1Id = h2hConfig.configuration.teams.team1.constructorId;
      const team2Id = h2hConfig.configuration.teams.team2.constructorId;
      
      // For Mercedes vs Ferrari, create realistic team data
      if (team1Id === 'mercedes' && team2Id === 'ferrari') {
        teamsData = {
          team1: {
            id: team1Id,
            name: h2hConfig.configuration.teams.team1.name,
            driversCount: 2,
            drivers: 'Hamilton (P5), Russell (P8)',
            positions: '5, 8',
            averagePosition: '6.5'
          },
          team2: {
            id: team2Id,
            name: h2hConfig.configuration.teams.team2.name,
            driversCount: 2,
            drivers: 'Leclerc (P3), Sainz (P4)',
            positions: '3, 4',
            averagePosition: '3.5'
          }
        };
        
        // Ferrari has better average, so they win
        calculated.results.teams.winner = team2Id;
      } else {
        // Mock team data with random positions
        const team1Positions = [
          Math.floor(Math.random() * 20) + 1,
          Math.floor(Math.random() * 20) + 1
        ];
        
        const team2Positions = [
          Math.floor(Math.random() * 20) + 1,
          Math.floor(Math.random() * 20) + 1
        ];
        
        const team1Avg = (team1Positions[0] + team1Positions[1]) / 2;
        const team2Avg = (team2Positions[0] + team2Positions[1]) / 2;
        
        teamsData = {
          team1: {
            id: team1Id,
            name: h2hConfig.configuration.teams.team1.name,
            driversCount: 2,
            drivers: `Driver 1 (P${team1Positions[0]}), Driver 2 (P${team1Positions[1]})`,
            positions: team1Positions.join(', '),
            averagePosition: team1Avg.toFixed(1)
          },
          team2: {
            id: team2Id,
            name: h2hConfig.configuration.teams.team2.name,
            driversCount: 2,
            drivers: `Driver 1 (P${team2Positions[0]}), Driver 2 (P${team2Positions[1]})`,
            positions: team2Positions.join(', '),
            averagePosition: team2Avg.toFixed(1)
          }
        };
        
        // Determine winner based on average position
        if (team1Avg < team2Avg) {
          calculated.results.teams.winner = team1Id;
        } else if (team2Avg < team1Avg) {
          calculated.results.teams.winner = team2Id;
        } else {
          // Tie
          calculated.results.teams.winner = 'tie';
        }
      }
    }
    
    setCalculatedResults(calculated);
    setDriverPositions(driversData);
    setTeamPositions(teamsData);
  };

  // Helper function to handle special cases for winners display
  const getWinnerDisplay = (resultType) => {
    if (!calculatedResults || !calculatedResults.results?.[resultType]) {
      return <div className="text-lg font-bold text-gray-500">Results Pending</div>;
    }
    
    const winner = calculatedResults.results[resultType].winner;
    
    if (winner === 'tie') {
      return <div className="text-lg font-bold text-gray-500">Tie (No Winner)</div>;
    } else if (winner === 'neither') {
      return <div className="text-lg font-bold text-gray-500">Neither Finished</div>;
    } else if (resultType === 'drivers') {
      return (
        <div className="text-lg font-bold text-green-700">
          {winner === calculatedResults.configuration.drivers.driver1.driverId
            ? calculatedResults.configuration.drivers.driver1.name
            : calculatedResults.configuration.drivers.driver2.name
          }
        </div>
      );
    } else if (resultType === 'teams') {
      return (
        <div className="text-lg font-bold text-green-700">
          {winner === calculatedResults.configuration.teams.team1.constructorId
            ? calculatedResults.configuration.teams.team1.name
            : calculatedResults.configuration.teams.team2.name
          }
        </div>
      );
    }
    
    return <div className="text-lg font-bold text-gray-500">Results Pending</div>;
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Debug Page</h1>
        <Link to="/admin" className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">
          Back to Admin
        </Link>
      </div>
      
      <div className="bg-white shadow-md rounded-lg p-6 max-w-4xl mx-auto mb-8">
        <h2 className="text-2xl font-bold mb-6">Head-to-Head Debug Tool</h2>
        
        <div className="mb-6">
          <label className="block mb-2 font-medium">Enter Race ID:</label>
          <div className="flex gap-4">
            <input 
              type="text"
              className="border p-2 rounded flex-1"
              value={raceId}
              onChange={(e) => setRaceId(e.target.value)}
              placeholder="Enter race ID here"
            />
            <button 
              className="bg-blue-500 text-white rounded px-4 py-2"
              onClick={fetchData}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Fetch Data'}
            </button>
          </div>
          
          {h2hConfig && (
            <div className="mt-4">
              <button 
                className="bg-green-500 text-white rounded px-4 py-2"
                onClick={simulateCalculation}
              >
                Simulate Calculation
              </button>
              <p className="mt-2 text-sm text-gray-600">
                This button simulates the calculation with realistic race positions for testing purposes.
              </p>
            </div>
          )}
          
          {error && (
            <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
        </div>
        
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
            <h3 className="text-lg font-semibold mb-2">Race IDs from Database</h3>
            <ul className="list-disc ml-5">
              <li><code>67df7835b142568bb5fddc74</code> - First race</li>
              <li><code>67df7835b142568bb5fddc76</code> - Second race</li>
            </ul>
          </div>
          
          <div className="p-4 bg-blue-50 border border-blue-200 rounded">
            <h3 className="text-lg font-semibold mb-2">Status</h3>
            <ul className="list-disc ml-5">
              <li>H2H Config: {h2hConfig ? '✅ Loaded' : '❌ Not loaded'}</li>
              <li>Race Results: {raceResults ? '✅ Loaded' : '❌ Not loaded'}</li>
              <li>Calculated Results: {calculatedResults ? '✅ Available' : '❌ Not available'}</li>
            </ul>
          </div>
        </div>
        
        {/* Driver Positions Section */}
        {driverPositions && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-3">Driver Position Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded border border-blue-200">
                <h4 className="font-medium mb-2">{driverPositions.driver1.name}</h4>
                <ul className="space-y-1">
                  <li><strong>Position:</strong> {driverPositions.driver1.position > 0 ? driverPositions.driver1.position : 'Not Found'}</li>
                  <li><strong>Found in results array:</strong> {driverPositions.driver1.foundInResults ? 'Yes' : 'No'}</li>
                  <li><strong>Team:</strong> {driverPositions.driver1.constructor}</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-4 rounded border border-blue-200">
                <h4 className="font-medium mb-2">{driverPositions.driver2.name}</h4>
                <ul className="space-y-1">
                  <li><strong>Position:</strong> {driverPositions.driver2.position > 0 ? driverPositions.driver2.position : 'Not Found'}</li>
                  <li><strong>Found in results array:</strong> {driverPositions.driver2.foundInResults ? 'Yes' : 'No'}</li>
                  <li><strong>Team:</strong> {driverPositions.driver2.constructor}</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-3 bg-yellow-50 p-3 rounded border border-yellow-200">
              <p className="text-sm">
                <strong>Analysis:</strong> Based on the positions above, the head-to-head calculation determined 
                {calculatedResults?.results?.drivers?.winner === 'neither' 
                  ? " that neither driver has a valid position in the race results. This suggests the drivers aren't in the race data or the data format is unexpected."
                  : calculatedResults?.results?.drivers?.winner === 'tie'
                    ? " that the drivers are tied."
                    : ` that ${driverPositions.driver1.id === calculatedResults?.results?.drivers?.winner 
                        ? driverPositions.driver1.name 
                        : driverPositions.driver2.name} is the winner.`
                }
              </p>
            </div>
          </div>
        )}
        
        {/* Team Positions Section */}
        {teamPositions && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-3">Team Position Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-indigo-50 p-4 rounded border border-indigo-200">
                <h4 className="font-medium mb-2">{teamPositions.team1.name}</h4>
                <ul className="space-y-1">
                  <li><strong>Drivers Found:</strong> {teamPositions.team1.driversCount}</li>
                  <li><strong>Drivers:</strong> {teamPositions.team1.drivers}</li>
                  <li><strong>Positions:</strong> {teamPositions.team1.positions}</li>
                  <li><strong>Average Position:</strong> {teamPositions.team1.averagePosition}</li>
                </ul>
              </div>
              
              <div className="bg-indigo-50 p-4 rounded border border-indigo-200">
                <h4 className="font-medium mb-2">{teamPositions.team2.name}</h4>
                <ul className="space-y-1">
                  <li><strong>Drivers Found:</strong> {teamPositions.team2.driversCount}</li>
                  <li><strong>Drivers:</strong> {teamPositions.team2.drivers}</li>
                  <li><strong>Positions:</strong> {teamPositions.team2.positions}</li>
                  <li><strong>Average Position:</strong> {teamPositions.team2.averagePosition}</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-3 bg-yellow-50 p-3 rounded border border-yellow-200">
              <p className="text-sm">
                <strong>Analysis:</strong> {teamPositions.team1.driversCount === 0 && teamPositions.team2.driversCount === 0 
                  ? "No drivers found for either team. This suggests the race data doesn't contain constructorId information to map drivers to teams."
                  : `Based on the team data, ${calculatedResults?.results?.teams?.winner === 'neither' 
                      ? "neither team has a valid position."
                      : calculatedResults?.results?.teams?.winner === 'tie'
                        ? "the teams are tied."
                        : `${teamPositions.team1.id === calculatedResults?.results?.teams?.winner 
                            ? teamPositions.team1.name 
                            : teamPositions.team2.name} is the winner.`
                      }`
                }
              </p>
            </div>
          </div>
        )}
        
        {/* Raw Data Sections */}
        <div className="space-y-6">
          {h2hConfig && (
            <div>
              <h3 className="text-xl font-semibold mb-2">H2H Configuration</h3>
              <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-60 text-xs">
                {JSON.stringify(h2hConfig, null, 2)}
              </pre>
            </div>
          )}
          
          {raceResults && (
            <div>
              <h3 className="text-xl font-semibold mb-2">Race Results</h3>
              <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-60 text-xs">
                {JSON.stringify(raceResults, null, 2)}
              </pre>
            </div>
          )}
          
          {calculatedResults && (
            <div>
              <h3 className="text-xl font-semibold mb-2">Calculated Results</h3>
              <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-60 text-xs">
                {JSON.stringify(calculatedResults.results, null, 2)}
              </pre>
            </div>
          )}
        </div>
        
        {/* Results Array Focus Section */}
        {/* Results Array Focus Section */}
        {raceResults && raceResults.result && raceResults.result.results && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded">
            <h3 className="text-lg font-semibold mb-2">Results Array Analysis</h3>
            <p className="mb-2">
              The race result contains a 'results' array with {raceResults.result.results.length} driver entries.
              This array should contain the position information for all drivers.
            </p>
            
            <div className="mt-4">
              <h4 className="font-medium mb-2">Sample Entries:</h4>
              <pre className="bg-white p-3 rounded text-xs overflow-auto max-h-40">
                {raceResults.result.results
                  .slice(0, 3) // Show first 3 entries
                  .map((driver, index) => (
                    JSON.stringify(driver, null, 2)
                  )).join('\n\n')}
              </pre>
              
              <div className="mt-4">
                <h4 className="font-medium mb-2">Search for Specific Drivers:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {h2hConfig && h2hConfig.configuration?.drivers && (
                    <div className="p-3 bg-white rounded">
                      <p className="font-medium">Search Results for H2H Drivers:</p>
                      <p className="mt-1 text-sm">
                        Driver 1 ({h2hConfig.configuration.drivers.driver1.driverId}): 
                        {raceResults.result.results.some(d => d.driverId === h2hConfig.configuration.drivers.driver1.driverId) 
                          ? ' Found ✅' 
                          : ' Not found ❌'}
                      </p>
                      <p className="mt-1 text-sm">
                        Driver 2 ({h2hConfig.configuration.drivers.driver2.driverId}): 
                        {raceResults.result.results.some(d => d.driverId === h2hConfig.configuration.drivers.driver2.driverId) 
                          ? ' Found ✅' 
                          : ' Not found ❌'}
                      </p>
                    </div>
                  )}
                  
                  {h2hConfig && h2hConfig.configuration?.teams && (
                    <div className="p-3 bg-white rounded">
                      <p className="font-medium">Search Results for H2H Teams:</p>
                      <p className="mt-1 text-sm">
                        Team 1 Drivers ({h2hConfig.configuration.teams.team1.constructorId}): 
                        {raceResults.result.results.filter(d => d.constructorId === h2hConfig.configuration.teams.team1.constructorId).length}
                      </p>
                      <p className="mt-1 text-sm">
                        Team 2 Drivers ({h2hConfig.configuration.teams.team2.constructorId}): 
                        {raceResults.result.results.filter(d => d.constructorId === h2hConfig.configuration.teams.team2.constructorId).length}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Problem Analysis & Solution */}
        {raceResults && h2hConfig && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
            <h3 className="text-lg font-semibold mb-2">Solution Recommendations</h3>
            
            <div className="space-y-4">
              {(!raceResults.result || !raceResults.result.results || raceResults.result.results.length === 0) ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="font-medium text-red-700">Issue: No results array found in race data.</p>
                  <p className="mt-1 text-sm">The race results data doesn't contain a results array with driver positions.</p>
                </div>
              ) : (
                <>
                  {h2hConfig.configuration?.drivers && 
                   !raceResults.result.results.some(d => d.driverId === h2hConfig.configuration.drivers.driver1.driverId) && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="font-medium text-yellow-800">
                        Issue: Driver 1 ({h2hConfig.configuration.drivers.driver1.driverId}) not found in results.
                      </p>
                      <p className="mt-1 text-sm">
                        Check if the driver ID in the head-to-head configuration matches the driver ID in the race results.
                      </p>
                    </div>
                  )}
                  
                  {h2hConfig.configuration?.drivers && 
                   !raceResults.result.results.some(d => d.driverId === h2hConfig.configuration.drivers.driver2.driverId) && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="font-medium text-yellow-800">
                        Issue: Driver 2 ({h2hConfig.configuration.drivers.driver2.driverId}) not found in results.
                      </p>
                      <p className="mt-1 text-sm">
                        Check if the driver ID in the head-to-head configuration matches the driver ID in the race results.
                      </p>
                    </div>
                  )}
                </>
              )}
              
              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <p className="font-medium text-green-800">Recommended Solution:</p>
                <ol className="mt-2 space-y-2 list-decimal ml-5 text-sm">
                  <li>
                    <strong>Client-side implementation:</strong> Use the results array from race data to find driver positions and calculate 
                    head-to-head winners as demonstrated in this debug page.
                  </li>
                  <li>
                    <strong>Update HeadToHeadResults.js:</strong> Modify the HeadToHeadResults component to use the same approach to 
                    calculate and display head-to-head winners.
                  </li>
                  <li>
                    <strong>Backend enhancement (future):</strong> Modify the backend to calculate and store head-to-head winners in the 
                    Result model when race results are processed.
                  </li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Component Preview Section */}
      {calculatedResults && (
        <div className="bg-white shadow-md rounded-lg p-6 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">Component Preview</h2>
          <p className="mb-4 text-gray-600">
            This is how the HeadToHeadResults component will look with the calculated results:
          </p>
          
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Head-to-Head Results</h2>
            
            {/* Driver Head-to-Head */}
            {calculatedResults.configuration?.drivers && (
              <div className="bg-white shadow rounded-lg p-4">
                <h3 className="text-lg font-medium mb-3">Driver Head-to-Head</h3>
                
                <div className="flex items-center justify-between">
                  {/* Driver 1 */}
                  <div className={`flex-1 p-3 rounded-lg text-center ${
                    calculatedResults.results?.drivers?.winner === calculatedResults.configuration.drivers.driver1.driverId
                      ? 'bg-green-100 border border-green-300'
                      : 'bg-gray-100'
                  }`}>
                    <div className="font-bold text-lg">
                      {calculatedResults.configuration.drivers.driver1.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {calculatedResults.configuration.drivers.driver1.code}
                    </div>
                    {driverPositions && (
                      <div className="mt-1 text-xs">
                        {driverPositions.driver1.position > 0 
                          ? `Position: ${driverPositions.driver1.position}` 
                          : 'Position: Not Found'}
                      </div>
                    )}
                  </div>
                  
                  {/* VS */}
                  <div className="px-4 font-bold text-xl">
                    VS
                  </div>
                  
                  {/* Driver 2 */}
                  <div className={`flex-1 p-3 rounded-lg text-center ${
                    calculatedResults.results?.drivers?.winner === calculatedResults.configuration.drivers.driver2.driverId
                      ? 'bg-green-100 border border-green-300'
                      : 'bg-gray-100'
                  }`}>
                    <div className="font-bold text-lg">
                      {calculatedResults.configuration.drivers.driver2.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {calculatedResults.configuration.drivers.driver2.code}
                    </div>
                    {driverPositions && (
                      <div className="mt-1 text-xs">
                        {driverPositions.driver2.position > 0 
                          ? `Position: ${driverPositions.driver2.position}` 
                          : 'Position: Not Found'}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Winner Display */}
                <div className="mt-4 text-center">
                  <div className="font-medium">Winner:</div>
                  {getWinnerDisplay('drivers')}
                </div>
              </div>
            )}
            
            {/* Team Head-to-Head */}
            {calculatedResults.configuration?.teams && (
              <div className="bg-white shadow rounded-lg p-4">
                <h3 className="text-lg font-medium mb-3">Team Head-to-Head</h3>
                
                <div className="flex items-center justify-between">
                  {/* Team 1 */}
                  <div className={`flex-1 p-3 rounded-lg text-center ${
                    calculatedResults.results?.teams?.winner === calculatedResults.configuration.teams.team1.constructorId
                      ? 'bg-green-100 border border-green-300'
                      : 'bg-gray-100'
                  }`}>
                    <div className="font-bold text-lg">
                      {calculatedResults.configuration.teams.team1.name}
                    </div>
                    {teamPositions && (
                      <div className="mt-1 text-xs">
                        {teamPositions.team1.averagePosition !== 'N/A' 
                          ? `Avg Pos: ${teamPositions.team1.averagePosition}` 
                          : 'No position data'}
                      </div>
                    )}
                  </div>
                  
                  {/* VS */}
                  <div className="px-4 font-bold text-xl">
                    VS
                  </div>
                  
                  {/* Team 2 */}
                  <div className={`flex-1 p-3 rounded-lg text-center ${
                    calculatedResults.results?.teams?.winner === calculatedResults.configuration.teams.team2.constructorId
                      ? 'bg-green-100 border border-green-300'
                      : 'bg-gray-100'
                  }`}>
                    <div className="font-bold text-lg">
                      {calculatedResults.configuration.teams.team2.name}
                    </div>
                    {teamPositions && (
                      <div className="mt-1 text-xs">
                        {teamPositions.team2.averagePosition !== 'N/A' 
                          ? `Avg Pos: ${teamPositions.team2.averagePosition}` 
                          : 'No position data'}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Winner Display */}
                <div className="mt-4 text-center">
                  <div className="font-medium">Winner:</div>
                  {getWinnerDisplay('teams')}
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded">
            <h3 className="text-lg font-semibold mb-2">Implementation Notes</h3>
            <p className="text-sm">
              This client-side calculation approach uses the race results array to find driver positions and calculate
              head-to-head winners. The same logic can be implemented in the HeadToHeadResults component to solve the
              issue with head-to-head results not appearing in the UI.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebugPage;